import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  sendEmailVerification as firebaseSendEmailVerification,
  reload as firebaseReload
} from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions, signInWithGoogle as firebaseSignInWithGoogle, signOutUser, linkGoogleToAccount } from '../firebase/config';

// Logger: solo emite en desarrollo, evita ruido en producción
const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

// Cachés en localStorage que guardan datos de UN restaurante (no scopeadas por id).
// Se limpian al cambiar de sede (super admin o dueño/gerente de cadena) para evitar
// que una sede muestre datos de otra. Ver Sucursales — Ola 1, fix de data-bleed.
const RESTAURANT_SCOPED_CACHE_KEYS = [
  'kumia_analytics_cache',
  'kumia_customers_cache',
  'kumia_consumer_analytics_cache',
  'shifts_cache',
  'staff_members_cache',
  'kumia_restaurant_config',
];
const clearRestaurantScopedCaches = () => {
  try {
    RESTAURANT_SCOPED_CACHE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (_e) {
    // localStorage puede no estar disponible (incógnito/SSR); ignorar.
  }
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [restaurantProfile, setRestaurantProfile] = useState(null);
  const [customClaims, setCustomClaims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [isCreatingGoogleProfile, setIsCreatingGoogleProfile] = useState(false);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(null);

  // Dedup: evitar re-lecturas de perfil cuando onAuthStateChanged dispara tras un flujo explícito
  const profileLoadCache = useRef({ uid: null, timestamp: 0 });
  const restaurantLoadCache = useRef({ id: null, timestamp: 0 });

  // Cloud Functions
  const createNewOwnerWithProfile = httpsCallable(functions, 'auth-endpoint-createNewOwnerWithProfile');
  const createNewOwnerWithGoogle = httpsCallable(functions, 'auth-endpoint-createNewOwnerWithGoogle');
  const completeOnboardingFunction = httpsCallable(functions, 'restaurants-endpoints-completeOnboarding');

  // Función para obtener Custom Claims actualizados
  // forceRefresh=true solo tras acciones que mutan claims (onboarding, link Google, etc.)
  const refreshCustomClaims = useCallback(async (user, forceRefresh = false) => {
    if (!user) {
      setCustomClaims(null);
      return null;
    }

    try {
      const idTokenResult = await user.getIdTokenResult(forceRefresh);

      const claims = idTokenResult.claims;
      // Retrocompatibilidad: soportar estructura antigua y nueva de claims
      const restaurantId = claims.ownerData?.restaurantId || claims.staffData?.restaurantId || claims.restaurantId;
      const roles = claims.roles || (claims.role ? [claims.role] : []);
      const onboardingComplete = claims.ownerData?.onboardingComplete ?? claims.onboardingComplete;

      devLog('📋 Custom Claims actualizados:', { restaurantId, roles, onboardingComplete });
      setCustomClaims(claims);
      return claims;
    } catch (error) {
      console.error('Error obteniendo Custom Claims:', error);
      return null;
    }
  }, []);

  // Función para obtener perfil de usuario desde Firestore
  const loadUserProfile = useCallback(async (uid) => {
    if (!uid) {
      setUserProfile(null);
      return null;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const profile = userDoc.data();
        devLog('👤 Perfil de usuario cargado');
        setUserProfile(profile);
        profileLoadCache.current = { uid, timestamp: Date.now() };
        return profile;
      } else {
        console.warn('Perfil de usuario no existe en Firestore');
        setUserProfile(null);
        return null;
      }
    } catch (error) {
      console.error('Error cargando perfil de usuario:', error);
      setUserProfile(null);
      return null;
    }
  }, []);

  // Función para obtener perfil del restaurante desde Firestore
  const loadRestaurantProfile = useCallback(async (restaurantId) => {
    if (!restaurantId) {
      setRestaurantProfile(null);
      return null;
    }

    try {
      const restaurantDoc = await getDoc(doc(db, 'restaurants', restaurantId));
      if (restaurantDoc.exists()) {
        const profile = {
          id: restaurantDoc.id,
          ...restaurantDoc.data()
        };
        devLog('🏪 Perfil de restaurante cargado');
        setRestaurantProfile(profile);
        restaurantLoadCache.current = { id: restaurantId, timestamp: Date.now() };
        return profile;
      } else {
        console.warn('Perfil de restaurante no existe en Firestore');
        setRestaurantProfile(null);
        return null;
      }
    } catch (error) {
      console.error('Error cargando perfil de restaurante:', error);
      setRestaurantProfile(null);
      return null;
    }
  }, []);

  // Calcular si el usuario necesita onboarding (como valor derivado)
  const needsOnboarding = React.useMemo(() => {
    if (!customClaims || !currentUser) return false;

    const roles = customClaims.roles || (customClaims.role ? [customClaims.role] : []);
    if (!roles.includes('owner')) return false;

    const onboardingComplete = customClaims.ownerData?.onboardingComplete ?? customClaims.onboardingComplete;
    return onboardingComplete === false;
  }, [customClaims, currentUser]);


  // Manejo de cambios en el estado de autenticación
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      devLog('🔔 Auth state changed:', user?.email || 'No user');

      setCurrentUser(user);

      if (user) {
        // Dedup: si el perfil fue cargado hace <5s por un flujo explícito, no re-leer
        const now = Date.now();
        const profileRecent = profileLoadCache.current.uid === user.uid
          && now - profileLoadCache.current.timestamp < 5000;

        const [claims, profile] = await Promise.all([
          refreshCustomClaims(user), // forceRefresh=false por defecto (usa token cacheado)
          profileRecent ? Promise.resolve(null) : loadUserProfile(user.uid)
        ]);

        const userRestaurantId = claims?.ownerData?.restaurantId || claims?.staffData?.restaurantId || claims?.restaurantId;

        if (userRestaurantId) {
          const restaurantRecent = restaurantLoadCache.current.id === userRestaurantId
            && now - restaurantLoadCache.current.timestamp < 5000;
          if (!restaurantRecent) {
            await loadRestaurantProfile(userRestaurantId);
          }
        }

        // Verificar si es un usuario nuevo de Google que necesita completar registro
        if (!profile && !profileRecent && user.providerData[0]?.providerId === 'google.com') {
          setIsNewUser(true);
        } else {
          setIsNewUser(false);
        }
      } else {
        setCustomClaims(null);
        setUserProfile(null);
        setRestaurantProfile(null);
        setIsNewUser(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, [refreshCustomClaims, loadUserProfile, loadRestaurantProfile]);

  // Función de registro con email/password
  const register = async (userData) => {
    try {
      setLoading(true);

      const { email, password, firstName, lastName, referredBy } = userData;
      const displayName = `${firstName} ${lastName}`;

      devLog('📝 Registrando usuario:', email);

      // Llamar Cloud Function para crear usuario
      const result = await createNewOwnerWithProfile({
        email,
        password,
        displayName,
        ...(referredBy && { referredBy })
      });

      if (result.data.success) {
        const userCredential = await signInWithCustomToken(auth, result.data.customToken);
        devLog('✅ Registro exitoso, usuario logueado');

        // Usar el usuario del UserCredential que acabamos de obtener
        const user = userCredential.user;

        if (!user) {
          return { success: true, emailVerificationSent: false };
        }

        try {
          await firebaseSendEmailVerification(user, {
            url: `${window.location.origin}/verify-email`,
            handleCodeInApp: true,
          });

          devLog('✅ Email de verificación enviado');
          return { success: true, emailVerificationSent: true };
        } catch (emailError) {
          console.error('Error enviando email de verificación:', emailError);
          return { success: true, emailVerificationSent: false, emailError: emailError.message };
        }
      } else {
        throw new Error(result.data.message || 'Error en el registro');
      }
    } catch (error) {
      console.error('❌ Error en registro:', error);

      let errorMessage = 'Error durante el registro';

      // Capturar el error específico cuando la cuenta ya existe con Google
      if (error.code === 'already-exists' || error.message?.includes('método de autenticación de Google')) {
        errorMessage = 'Esta cuenta fue creada con el método de autenticación de Google. Para configurar una contraseña, accede con Google y defínela desde configuración de usuario.';
        return {
          success: false,
          error: errorMessage,
          needsGoogleLogin: true // Flag para que el frontend sepa que debe sugerir login con Google
        };
      }

      // Otros errores comunes
      if (error.code === 'auth/email-already-in-use' || error.message?.includes('ya está en uso')) {
        errorMessage = 'Este email ya está registrado. Por favor, inicia sesión.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'El formato del email no es válido.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'La contraseña debe tener al menos 6 caracteres.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función de login con email/password
  const login = async (email, password) => {
    try {
      setLoading(true);
      devLog('🔐 Iniciando sesión:', email);

      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      console.error('Error en login:', error.code);

      let errorMessage = 'Error de autenticación';

      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/invalid-login-credentials':
          // Mensaje genérico sin consultar Firestore — evita lecturas innecesarias
          // y previene enumeración de emails
          errorMessage = 'Credenciales inválidas. Verifica tu email y contraseña. Si creaste tu cuenta con Google, usa el botón "Iniciar sesión con Google".';
          break;
        case 'auth/user-not-found':
          errorMessage = 'Usuario no encontrado';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Email inválido';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Cuenta deshabilitada';
          break;
        default:
          errorMessage = error.message || 'Error de autenticación';
      }

      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función de login con Google
  // referredBy (opcional): UID del cliente Kumia-Web que refirió (link de referido ?ref=)
  const googleLogin = async (referredBy = null) => {
    try {
      setLoading(true);
      devLog('🔍 Iniciando sesión con Google');

      const result = await firebaseSignInWithGoogle();

      const hasPasswordProvider = result.user.providerData.some(
        provider => provider.providerId === 'password'
      );
      const hasGoogleProvider = result.user.providerData.some(
        provider => provider.providerId === 'google.com'
      );

      // Verificar si tiene perfil en Firestore
      const profileCheck = await loadUserProfile(result.user.uid);

      // CASO 1: Usuario completamente nuevo (no tiene perfil en Firestore)
      if (!profileCheck) {
        devLog('📝 Usuario nuevo de Google detectado, creando perfil...');
        setIsCreatingGoogleProfile(true);
        sessionStorage.setItem('creatingGoogleProfile', 'true');

        try {
          const response = await createNewOwnerWithGoogle(referredBy ? { referredBy } : {});
          const { isNewOwner, restaurantId: newRestaurantId } = response.data;

          // Forzar refresh de claims — la Cloud Function acaba de mutar claims
          await refreshCustomClaims(result.user, true);
          await loadUserProfile(result.user.uid);

          if (isNewOwner && newRestaurantId) {
            await loadRestaurantProfile(newRestaurantId);
          }

          sessionStorage.removeItem('creatingGoogleProfile');
          setIsCreatingGoogleProfile(false);

          return {
            success: true,
            isNewUser: isNewOwner,
            restaurantId: newRestaurantId
          };

        } catch (createError) {
          console.error('Error creando perfil con Google:', createError);
          setIsCreatingGoogleProfile(false);
          sessionStorage.removeItem('creatingGoogleProfile');
          throw createError;
        }
      }
      // CASO 2: Usuario existente con password que vinculó Google (linkWithPopup)
      else if (profileCheck.authentication?.provider === 'password' && hasPasswordProvider && hasGoogleProvider) {
        devLog('🔗 Usuario de password vinculó Google exitosamente');

        try {
          await createNewOwnerWithGoogle();
        } catch (updateError) {
          console.warn('Error actualizando vinculación en Firestore:', updateError);
        }

        // Forzar refresh — la Cloud Function pudo mutar claims
        await refreshCustomClaims(result.user, true);

        const userRestaurantId = profileCheck.ownerData?.restaurantId || profileCheck.staffData?.restaurantId || profileCheck.restaurantId;
        if (userRestaurantId) {
          await loadRestaurantProfile(userRestaurantId);
        }
      }
      // CASO 3: Usuario existente normal (ya tiene cuenta de Google)
      else {
        devLog('👤 Usuario existente con Google - login normal');

        // No forzar refresh — los claims no cambiaron
        const claims = await refreshCustomClaims(result.user);

        const userRestaurantId = profileCheck.ownerData?.restaurantId || profileCheck.staffData?.restaurantId || profileCheck.restaurantId || claims?.ownerData?.restaurantId || claims?.staffData?.restaurantId || claims?.restaurantId;
        if (userRestaurantId) {
          await loadRestaurantProfile(userRestaurantId);
        }
      }

      return { success: true };
    } catch (error) {
      console.error('Error en Google login:', error.code);
      setIsCreatingGoogleProfile(false);
      sessionStorage.removeItem('creatingGoogleProfile');

      if (error.code === 'auth/account-exists-with-different-credential') {
        return {
          success: false,
          error: 'Esta cuenta ya existe con email y contraseña. Por favor, inicia sesión con tu contraseña primero y luego vincula Google desde Configuración.',
          needsPasswordLogin: true
        };
      }

      return { success: false, error: error.message || 'Error de autenticación con Google' };
    } finally {
      setLoading(false);
    }
  };

  // Función para vincular Google a cuenta existente (desde Settings)
  const linkGoogle = async () => {
    try {
      if (!currentUser) {
        throw new Error('No hay usuario autenticado');
      }

      setLoading(true);
      devLog('🔗 Vinculando Google a cuenta existente...');

      await linkGoogleToAccount(currentUser);

      try {
        await createNewOwnerWithGoogle();
      } catch (firestoreError) {
        console.warn('Error actualizando Firestore en linkGoogle:', firestoreError);
      }

      await currentUser.reload();
      // Forzar refresh — la Cloud Function pudo mutar claims
      await refreshCustomClaims(currentUser, true);
      await loadUserProfile(currentUser.uid);

      return {
        success: true,
        message: 'Google vinculado correctamente. Ahora puedes iniciar sesión con Google también.'
      };
    } catch (error) {
      console.error('Error vinculando Google:', error.code);

      const errorMessages = {
        'auth/credential-already-in-use': 'Esta cuenta de Google ya está vinculada a otro usuario.',
        'auth/provider-already-linked': 'Google ya está vinculado a esta cuenta.',
        'auth/email-already-in-use': 'Este email de Google ya está en uso por otra cuenta.',
      };

      return {
        success: false,
        error: errorMessages[error.code] || error.message || 'Error vinculando Google a la cuenta'
      };
    } finally {
      setLoading(false);
    }
  };

  // Función de logout
  const logout = async () => {
    try {
      await signOutUser();
      // Limpiar caches de dedup
      profileLoadCache.current = { uid: null, timestamp: 0 };
      restaurantLoadCache.current = { id: null, timestamp: 0 };
      return { success: true };
    } catch (error) {
      console.error('Error en logout:', error);
      return { success: false, error: error.message };
    }
  };

  // Función para enviar email de verificación
  const sendEmailVerification = async () => {
    try {
      if (!currentUser) {
        return { success: false, message: 'No hay usuario autenticado' };
      }

      if (currentUser.emailVerified) {
        return { success: false, message: 'El email ya está verificado' };
      }

      await firebaseSendEmailVerification(currentUser, {
        url: `${window.location.origin}/verify-email`,
        handleCodeInApp: true,
      });

      devLog('✅ Email de verificación enviado a:', currentUser.email);

      return {
        success: true,
        message: 'Correo de verificación enviado. Revisa tu bandeja de entrada.'
      };
    } catch (error) {
      console.error('Error enviando email de verificación:', error);

      if (error.code === 'auth/too-many-requests') {
        return {
          success: false,
          message: 'Demasiados intentos. Espera unos minutos.'
        };
      }

      return {
        success: false,
        message: 'No se pudo enviar el correo. Inténtalo de nuevo.'
      };
    }
  };

  // Función para verificar y continuar
  const verifyAndContinue = async () => {
    try {
      if (!currentUser) return false;

      await firebaseReload(currentUser);

      if (currentUser.emailVerified) {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          await updateDoc(userRef, {
            emailVerified: true,
            emailVerifiedAt: new Date(),
          });

          // Forzar refresh — emailVerified cambia el token
          await refreshCustomClaims(currentUser, true);
        } catch (updateError) {
          console.error('Error actualizando Firestore en verifyAndContinue:', updateError);
        }
      }

      return currentUser.emailVerified;
    } catch (error) {
      console.error('Error en verifyAndContinue:', error);
      return false;
    }
  };

  // Función para completar onboarding
  const completeOnboarding = async (onboardingData) => {
    try {
      devLog('📋 Completando onboarding...');

      const result = await completeOnboardingFunction(onboardingData);

      if (result.data.success) {
        // Forzar refresh — la Cloud Function acaba de mutar claims (onboardingComplete: true)
        const updatedClaims = await refreshCustomClaims(currentUser, true);

        if (currentUser) {
          await loadUserProfile(currentUser.uid);
        }

        const restaurantId = updatedClaims?.ownerData?.restaurantId || updatedClaims?.staffData?.restaurantId || updatedClaims?.restaurantId;
        if (restaurantId) {
          await loadRestaurantProfile(restaurantId);
        }

        return { success: true, restaurantId };
      } else {
        throw new Error(result.data.message || 'Error completando onboarding');
      }
    } catch (error) {
      console.error('Error completando onboarding:', error);
      return { success: false, error: error.message };
    }
  };

  // Función para cambiar de restaurante/sede (super admin o dueño/gerente de cadena).
  // Limpia las cachés scopeadas por restaurante para evitar fuga de datos entre sedes.
  const switchRestaurant = useCallback(async (newRestaurantId) => {
    clearRestaurantScopedCaches();
    setSelectedRestaurantId(newRestaurantId);
    await loadRestaurantProfile(newRestaurantId);
  }, [loadRestaurantProfile]);

  // Función para verificar acceso a un restaurante/sede desde la URL.
  // Guard de UX (el backend es el límite de seguridad real vía assertPermission/reglas).
  const verifyRestaurantAccess = useCallback((restaurantIdFromUrl) => {
    if (!customClaims || !restaurantIdFromUrl) return false;

    if (customClaims.superAdmin === true) return true;

    // Sucursales: dueño/gerente de cadena accede a las sedes de su organización.
    // Se valida contra los IDs denormalizados en el perfil de usuario (Firestore),
    // NO contra el claim → sin staleness al agregar una sede.
    const orgData = customClaims.orgData;
    if (orgData?.orgId) {
      // En un deep-link/refresh el perfil (ownedRestaurantIds/orgAccess) puede no
      // haber cargado aún; permitimos mientras carga (el backend es el límite real)
      // y afinamos cuando esté disponible.
      if (!userProfile) return true;
      if (orgData.role === 'group_owner') {
        if (userProfile?.ownedRestaurantIds?.includes(restaurantIdFromUrl)) return true;
      } else if (orgData.role === 'group_admin') {
        const scope = orgData.scope;
        // scope 'ALL': guard permisivo; el backend valida que la sede sea de su org.
        if (scope === 'ALL') return true;
        if (Array.isArray(scope) && scope.includes(restaurantIdFromUrl)) return true;
        const access = userProfile?.orgAccess?.find((a) => a.orgId === orgData.orgId);
        if (access && (access.locationIds === 'ALL'
            || (Array.isArray(access.locationIds) && access.locationIds.includes(restaurantIdFromUrl)))) {
          return true;
        }
      }
    }

    // Verificar que el restaurantId de la URL coincida con el del usuario
    // Buscar en ownerData (para owners) o staffData (para miembros del equipo)
    const userRestaurantId = customClaims.ownerData?.restaurantId || customClaims.staffData?.restaurantId || customClaims.restaurantId;

    if (userRestaurantId !== restaurantIdFromUrl) {
      devLog('🚫 Acceso denegado: restaurantId no coincide');
      return false;
    }

    return true;
  }, [customClaims, userProfile]);

  // Valores del contexto
  const value = {
    // Estado
    currentUser,
    userProfile,
    restaurantProfile,
    customClaims,
    loading,
    isNewUser,
    isCreatingGoogleProfile,
    isEmailVerified: currentUser?.emailVerified || false,

    // Funciones de autenticación
    register,
    login,
    googleLogin,
    linkGoogle,
    logout,
    sendEmailVerification,
    verifyAndContinue,

    // Funciones de onboarding
    completeOnboarding,
    needsOnboarding,

    // Funciones de autorización
    verifyRestaurantAccess,

    // Super Admin
    isSuperAdmin: customClaims?.superAdmin === true,
    switchRestaurant,
    selectedRestaurantId,
    superAdminSelectedRestaurantId: selectedRestaurantId, // alias retrocompat

    // Sucursales (organizaciones / cadenas)
    hasOrg: !!customClaims?.orgData?.orgId,
    orgId: customClaims?.orgData?.orgId || null,
    orgRole: customClaims?.orgData?.role || null,
    isGroupOwner: customClaims?.orgData?.role === 'group_owner',
    isGroupAdmin: customClaims?.orgData?.role === 'group_admin',

    // Suspension check: el restaurante está suspendido y el usuario NO es super admin
    isRestaurantSuspended: restaurantProfile?.status === 'suspend' && customClaims?.superAdmin !== true,

    // Utilidades
    refreshCustomClaims: () => refreshCustomClaims(currentUser),
    loadRestaurantProfile,
    // Retrocompatibilidad: soportar roles (array) y role (string)
    isOwner: customClaims?.roles?.includes('owner') || customClaims?.role === 'owner',
    isAdmin: customClaims?.roles?.includes('admin') || customClaims?.role === 'admin',
    restaurantId: ((customClaims?.superAdmin === true || !!customClaims?.orgData?.orgId) && selectedRestaurantId)
      ? selectedRestaurantId
      : (customClaims?.ownerData?.restaurantId || customClaims?.staffData?.restaurantId || customClaims?.restaurantId)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;

